import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useMemo, useState, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "ui";
import { AccordionContent, AccordionItem, AccordionPrimitive } from "ui";
import { Button } from "ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "ui";
import { assert } from "utils";

import {
  getSectionFormatLabel,
  getSectionTypeLabel,
} from "src/components/SectionInlineFields";
import { EditableGroupName } from "src/workouts/DraftWorkoutItemView";
import { SectionFormatIcon } from "src/workouts/SectionFormatIcon";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { type WorkoutItemLayout } from "src/workouts/WorkoutEditStore";
import {
  GroupWorkoutItemActionsProvider,
  WorkoutItemActions,
} from "src/workouts/WorkoutItemActions";
import {
  workoutItemGroupContext,
  type WorkoutItemGroupContextValue,
} from "src/workouts/WorkoutItemGroupContext";
import { getWorkoutItemElementId } from "src/workouts/workoutItemScroll";
import { useWorkoutPermissions } from "src/workouts/WorkoutItemsPermission";
import { WorkoutItemWrapper } from "src/workouts/WorkoutItemWrapper";

export interface WorkoutItemGroupProps extends PropsWithChildren {
  id: string;
  item: WorkoutItemLayout;
  index: number;
  childCount: number;
  level: number;
  isLast: boolean;
  previousItemId?: string;
  header?: React.ReactNode;
  body?: React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
}

export const BaseDetailedWorkoutItemGroup = observer(
  function BaseDetailedWorkoutItemGroup(props: WorkoutItemGroupProps) {
    const { t } = useTranslation();

    const workoutEditStore = useWorkoutEditStore();
    const workoutItemPermissions = useWorkoutPermissions();

    const workoutItem = workoutEditStore.getGroupWorkoutItemById(
      props.item.workoutItemId,
    );

    const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);
    const format = workoutItem.format;
    const groupType = workoutItem.groupType;

    const contextValue = useMemo<WorkoutItemGroupContextValue>(() => {
      return {
        get wrapperRef() {
          assert(wrapperRef !== null);
          return wrapperRef;
        },
        format: format,
        groupType: groupType,
      };
    }, [wrapperRef, format, groupType]);

    const formatLabel =
      getSectionFormatLabel(format, t) || getSectionTypeLabel(groupType, t);

    return (
      <workoutItemGroupContext.Provider value={contextValue}>
        <WorkoutItemWrapper
          ref={setWrapperRef}
          level={props.level}
          value={props.id}
          as={AccordionItem}
          className={cn(
            "shadow-none bg-slate-50/50 border border-slate-200 scroll-mt-20",
            props.wrapperClassName,
          )}
          id={getWorkoutItemElementId(workoutItem.id)}
        >
          <div
            className={cn("gap-2 w-full flex flex-col group", props.className)}
          >
            <div
              className={cn(
                "flex items-center w-full gap-2 bg-slate-100/80 p-2 py-1 rounded-tr-[12px] rounded-tl-[12px]",
                props.headerClassName,
              )}
            >
              <AccordionPrimitive.Header>
                <AccordionPrimitive.Trigger asChild>
                  <Button
                    variant={"ghost"}
                    className={cn(
                      "p-0 rounded-[4px] h-7 w-7 shrink-0",
                      "transition-all [&[data-state=open]>svg]:rotate-180",
                    )}
                  >
                    <Icon
                      name="tabler-chevron-down"
                      className={"h-4 w-4 transition-transform duration-200"}
                    />
                  </Button>
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              {formatLabel ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="shrink-0 cursor-default">
                      <SectionFormatIcon format={format} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    {formatLabel}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <SectionFormatIcon format={format} />
              )}
              <div className={"min-w-0 flex-1"}>
                {workoutItemPermissions.canEditItems ? (
                  <EditableGroupName workoutItemId={workoutItem.id} />
                ) : (
                  <div>
                    {workoutEditStore.getWorkoutItemName(workoutItem.id) ||
                      t("editWorkout.defaultGroupName")}
                  </div>
                )}
              </div>
              {props.header}
              <GroupWorkoutItemActionsProvider workoutItemId={workoutItem.id}>
                <WorkoutItemActions />
              </GroupWorkoutItemActionsProvider>
            </div>
          </div>
          <AccordionContent
            className={cn("flex flex-col gap-2 p-2", props.contentClassName)}
          >
            {props.body}
            {props.children}
          </AccordionContent>
        </WorkoutItemWrapper>
      </workoutItemGroupContext.Provider>
    );
  },
);
